import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

const ProductSearchDropdown = ({ products, onSelect, placeholder = "Search by SKU or Product Name..." }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products when search changes
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(product => {
        const skuMatch = product.sku_code?.toLowerCase().includes(query);
        const modelMatch = product.model_name?.toLowerCase().includes(query);
        return skuMatch || modelMatch;
      });
      setFilteredResults(filtered);
      setHighlightedIndex(0);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredResults([]);
      setIsOpen(false);
    }
  }, [searchQuery, products]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredResults[highlightedIndex]) {
          handleSelect(filteredResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelect = (product) => {
    setSearchQuery('');
    setIsOpen(false);
    onSelect(product);
  };

  const handleClear = () => {
    setSearchQuery('');
    setFilteredResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery && filteredResults.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <Search className="w-5 h-5 text-muted-foreground absolute left-3 top-2.5" />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && filteredResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs text-muted-foreground px-3 py-2">
              Found {filteredResults.length} product{filteredResults.length !== 1 ? 's' : ''}
            </div>
            {filteredResults.map((product, index) => (
              <button
                key={product.id}
                onClick={() => handleSelect(product)}
                className={`w-full text-left px-3 py-3 rounded-md transition-colors ${
                  index === highlightedIndex
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-center gap-3">
                  {product.images && product.images[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.model_name}
                      className="w-12 h-12 object-contain rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                      <Search className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${
                      index === highlightedIndex ? 'text-primary-foreground' : 'text-foreground'
                    }`}>
                      {product.model_name}
                    </div>
                    <div className={`text-sm truncate ${
                      index === highlightedIndex ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}>
                      SKU: {product.sku_code}
                    </div>
                    {product.brand_name && (
                      <div className={`text-xs truncate ${
                        index === highlightedIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        Brand: {product.brand_name}
                      </div>
                    )}
                  </div>
                  {/* Status Badge */}
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      product.is_active
                        ? 'bg-success/10 text-success'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {product.live_date && (
                      <span className="text-xs text-muted-foreground">
                        Live: {new Date(product.live_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && searchQuery && filteredResults.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">No products found</p>
          <p className="text-xs text-muted-foreground mt-1">Try searching by SKU code or product name</p>
        </div>
      )}
    </div>
  );
};

export default ProductSearchDropdown;
